import React, {useState, useEffect} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	FormControlLabel,
	Switch,
	Typography,
	Box,
	Grid,
	Paper
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import API from "../../../../network/dashboardAPI";
import UploadService from "../../../../services/FileUploadService";
import useAlert from "../../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	contentPaper: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc"
	},
	field: {
		marginBottom: theme.spacing(2)
	},
	uploadButton: {
		marginTop: theme.spacing(1)
	},
	imagePreview: {
		width: 380,
		height: 380,
		borderRadius: 12,
		objectFit: "cover",
		marginTop: theme.spacing(1),
		border: "1px solid #ddd"
	},
	actions: {
		padding: theme.spacing(2)
	},
	saveButton: {
		minWidth: 120
	}
}));

export default function DelipointTierDialog({open, onClose, onSaved, initialData}) {
	const classes = useStyles();
	const alert = useAlert();

	const [name, setName] = useState("");
	const [nameKh, setNameKh] = useState("");
	const [description, setDescription] = useState("");
	const [descriptionKh, setDescriptionKh] = useState("");
	const [tierOrderValue, setTierOrderValue] = useState("");
	const [multiplier, setMultiplier] = useState("");
	const [isDeleted, setIsDeleted] = useState(false);
	const [imagePreview, setImagePreview] = useState("");
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (initialData) {
			setName(initialData.name || "");
			setNameKh(initialData.name_kh || "");
			setDescription(initialData.description || "");
			setDescriptionKh(initialData.description_kh || "");
			setTierOrderValue(initialData.tier_order_value ?? "");
			setMultiplier(initialData.multiplier ?? "");
			setIsDeleted(initialData.is_deleted ?? false);
			setImagePreview(initialData.image || "");
		} else {
			setName("");
			setNameKh("");
			setDescription("");
			setDescriptionKh("");
			setTierOrderValue("");
			setMultiplier("");
			setIsDeleted(false);
			setImagePreview("");
		}
		setErrors({});
	}, [initialData, open]);

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			const formData = new FormData();
			formData.append("image", file);

			UploadService.uploadImageFromDashboard(formData).then((response) => {
				if (response.data.success) {
					setImagePreview(response.data.data[0].url);
				}
			});
		}
	};

	const validate = () => {
		const newErrors = {};
		if (!name) newErrors.name = "Name (EN) is required";
		if (!tierOrderValue) newErrors.tier_order_value = "Tier order value is required";
		if (!multiplier) newErrors.multiplier = "Multiplier is required";
		// description and descriptionKh are nullable, so no validation here
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;

		try {
			setLoading(true);

			const payload = {
				delipointId: initialData?._id,
				name,
				name_kh: nameKh,
				description,
				description_kh: descriptionKh,
				tier_order_value: tierOrderValue,
				multiplier,
				is_deleted: isDeleted,
				image: imagePreview
			};

			let response;
			const isEdit = !!initialData?._id;

			console.log("Creating delipoint with payload:", payload);
			if (isEdit) {
				response = await API.updateDelipointTier(payload);
			} else {
				response = await API.addDelipointTier(payload);
			}

			const res = await response.json();

			// Close loading alert
			alert.close();

			if (res.success) {
				const action = isEdit ? "edit" : "add";
				const itemName = name || initialData?.name;

				// Show success message
				alert.success(
					`${isEdit ? "Updated Successfully!" : "Created Successfully!"}`,
					`"${itemName}" has been ${isEdit ? "updated" : "created"} successfully.`
				);

				// Show toast notification
				alert.toast(`Delipoint ${isEdit ? "updated" : "created"}`, 'success');

				// Call parent callback with action and item name
				onSaved(action, itemName);
				onClose();
			} else {
				onClose();
				// Handle API error
				const errorMessage = res.message || `Failed to ${isEdit ? "update" : "create"} delipoint`;
				console.error(errorMessage);

				alert.error(
					`${isEdit ? "Update Failed" : "Creation Failed"}`,
					errorMessage
				);
			}
		} catch (err) {
			onClose();
			console.error("Error saving delipoints:", err);

			// Close any open loading alert
			alert.close();

			// Handle network error
			alert.error(
				"Network Error",
				`Unable to ${initialData?._id ? "update" : "create"} delipoint. Please check your connection and try again.`
			);
		} finally {
			setLoading(false);
		}
	};


	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{initialData ? "Edit Delipoint Tier" : "Add Delipoint Tier"}
			</DialogTitle>
			<DialogContent>
				<Paper className={classes.contentPaper}>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Name (EN)"
								fullWidth
								variant="outlined"
								className={classes.field}
								value={name}
								onChange={(e) => setName(e.target.value)}
								error={!!errors.name}
								helperText={errors.name}
							/>
							<TextField
								label="Name (KH)"
								fullWidth
								variant="outlined"
								className={classes.field}
								value={nameKh}
								onChange={(e) => setNameKh(e.target.value)}
							/>
							<TextField
								label="Tier Order Value"
								fullWidth
								variant="outlined"
								type="number"
								className={classes.field}
								value={tierOrderValue}
								onChange={(e) => setTierOrderValue(e.target.value)}
								error={!!errors.tier_order_value}
								helperText={errors.tier_order_value}
							/>
							<TextField
								label="Multiplier"
								fullWidth
								variant="outlined"
								type="number"
								className={classes.field}
								value={multiplier}
								onChange={(e) => setMultiplier(e.target.value)}
								error={!!errors.multiplier}
								helperText={errors.multiplier}
							/>
							<TextField
								label="Description (EN)"
								fullWidth
								variant="outlined"
								multiline
								rows={4}
								className={classes.field}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								// No error/required for nullable field
							/>
							<TextField
								label="Description (KH)"
								fullWidth
								variant="outlined"
								multiline
								rows={4}
								className={classes.field}
								value={descriptionKh}
								onChange={(e) => setDescriptionKh(e.target.value)}
								// No error/required for nullable field
							/>
							<FormControlLabel
								control={
									<Switch
										checked={isDeleted}
										onChange={(e) => setIsDeleted(e.target.checked)}
										color="primary"
									/>
								}
								label="Mark as Deleted"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Box display="flex" flexDirection="column" alignItems="center" mt={2}>
								<input
									accept="image/*"
									type="file"
									id="dietary-image-upload"
									style={{display: "none"}}
									onChange={handleImageChange}
								/>
								<label htmlFor="dietary-image-upload" style={{width: "100%"}}>
									<Button
										variant="outlined"
										color="primary"
										component="span"
										fullWidth
										style={{
											borderStyle: "dashed",
											borderWidth: 2,
											padding: "20px",
											textTransform: "none",
											borderRadius: 12,
										}}
									>
										{imagePreview ? "Change Image" : "Click to Upload Image"}
									</Button>
								</label>

								{imagePreview && (
									<Box
										mt={2}
										p={1.5}
										border="1px solid #e0e0e0"
										borderRadius={12}
										boxShadow={1}
										display="flex"
										flexDirection="column"
										alignItems="center"
										maxWidth={250}
										width="100%"
									>
										<img
											src={imagePreview}
											alt="Preview"
											style={{
												width: "100%",
												height: "auto",
												borderRadius: 8,
												objectFit: "cover",
											}}
										/>
										<Typography
											variant="caption"
											color="textSecondary"
											style={{marginTop: 8}}
										>
											Preview
										</Typography>
									</Box>
								)}
							</Box>

						</Grid>
					</Grid>
				</Paper>
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button onClick={onClose} color="default" variant="outlined" disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					color="primary"
					variant="contained"
					className={classes.saveButton}
					disabled={loading}
				>
					{loading ? "Saving..." : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
